import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, CUIText, Icon, IconNames } from '@dotcom/crown-ui';
import { useAppContext } from '../../../context/app-context';
import { useCardContext } from '../../../context/card-context';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useWamImageUpload } from '../../../hooks/use-wam-image-upload';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { useToolbarPosition } from '../toolbar-action-menu';
import { FooterContent } from './fragments/footer-content';
import { InstructionStepCompact } from './fragments/instruction-step-compact';
import { getWamInstructionsMenuHost } from './wam-instructions-menu-host';
import { WamInstructionsMenuProps } from './wam-instructions-menu-types';
import styles from './wam-instructions-menu.module.scss';

export const WamInstructionsMenu = ({
  onClose,
  isOpen,
  onImageSelect,
  onCrop,
}: WamInstructionsMenuProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const {
    appState: { isMobileApp },
  } = useAppContext();
  const {
    cardState: { activeCardIndex },
  } = useCardContext();
  const canvas = useActiveCanvas();
  const { steps, inputRef, buttonRef, handleImageUpload } = useWamImageUpload({ onClose, onImageSelect, onCrop });

  const isMobile = Boolean(isMobileApp);

  useToolbarPosition({
    getHost: getWamInstructionsMenuHost,
    getAnchor: () => canvas?.current?.getElement()?.closest<HTMLElement>('[data-testid^="canvas-wrapper-"]') ?? null,
    enabled: !isMobile && isOpen,
    measurementKey: activeCardIndex,
  });

  useEffect(() => {
    buttonRef.current?.focus();
  }, [isOpen, buttonRef]);

  const body = (
    <div className={styles['body-content']}>
      <div className={styles.header}>
        <CUIText variant="h4" as="h2" className={styles['drawer-title']}>
          {t('writeMessage.drawerTitle')}
        </CUIText>
        <Button
          variant="secondary"
          size="icon-sm"
          onClick={onClose}
          aria-label={t('writeMessage.closeAriaLabel') as string}
          className={styles['close-button']}
        >
          <Icon name={IconNames.Menuclose} color="purple" size="12" />
        </Button>
      </div>
      <div className={styles['steps-list']}>
        {steps.map(({ imageUrl, title, text }, index) => (
          <InstructionStepCompact
            key={`wam-instruction-${index + 1}`}
            imageUrl={imageUrl}
            text={text}
            title={title}
            stepNumber={index + 1}
          />
        ))}
      </div>
      <FooterContent
        inputRef={inputRef}
        buttonRef={buttonRef}
        handleImageUpload={handleImageUpload}
        footerContentText={t('writeMessage.footerContent')}
      />
    </div>
  );

  if (isMobileApp) {
    return (
      <Drawer
        id="wam-instructions-menu"
        drawerPosition={DrawerPositions.Bottom}
        onClose={onClose}
        isOpen={isOpen}
        addClass={styles['wam-instructions-menu']}
        hideCloseButton
        trapFocus
      >
        {body}
      </Drawer>
    );
  }

  const host = getWamInstructionsMenuHost();
  if (!host || !isOpen) {
    return null;
  }

  return createPortal(
    <div
      className={`${styles['wam-instructions-menu']} ${styles['floating-panel']}`}
      role="dialog"
      aria-label={t('writeMessage.drawerTitle') as string}
      data-testid="wam-instructions-menu-panel"
    >
      {body}
    </div>,
    host,
  );
};
